import { useEffect, useState } from "react";
import { Header, Menu, Segment } from "semantic-ui-react";
import { AsyncActionStatus } from "../models/AsyncActionStatus";
import { Organization } from "../models/Organization";
import { PermitBlueprint } from "../models/PermitBlueprint";
import { toastSuccess } from "../utils";
import AddBlueprintToOrganizationModal from "./AddBlueprintToOrganizationModal";
import EditPermitTemplateModal from "./EditPermitTemplateModal";
import PermitBlueprintsGrid from "./PermitBlueprintsGrid";

interface Props {
  isLoading: boolean;
  blueprints?: PermitBlueprint[];
  organizations?: Organization[];
  onAdd: (name: string, templateJson?: string) => void;
  addStatus: AsyncActionStatus;
  onEdit: (id: string, name: string, templateJson?: string) => void;
  editStatus: AsyncActionStatus;
  onAddToOrganization: (blueprintId: string, organizationId: string, permitTypeName?: string) => void;
  addToOrganizationStatus: AsyncActionStatus;
}

const PermitBlueprintsList = ({
  isLoading,
  blueprints,
  organizations,
  onAdd,
  addStatus,
  onEdit,
  editStatus,
  onAddToOrganization,
  addToOrganizationStatus,
}: Props) => {
  const [openAddModal, setOpenAddModal] = useState(false);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [openAddToOrgModal, setOpenAddToOrgModal] = useState(false);
  const [selectedBlueprint, setSelectedBlueprint] = useState<PermitBlueprint | undefined>(undefined);

  const handleEditClicked = (blueprintId: string) => {
    const blueprint = blueprints?.find((b) => b.id === blueprintId);
    if (blueprint) {
      setSelectedBlueprint({ ...blueprint });
      setOpenEditModal(true);
    }
  };

  const handleAddToOrganizationClicked = (blueprintId: string) => {
    const blueprint = blueprints?.find((b) => b.id === blueprintId);
    if (blueprint) {
      setSelectedBlueprint({ ...blueprint });
      setOpenAddToOrgModal(true);
    }
  };

  const handleModalsClose = () => {
    setOpenAddModal(false);
    setOpenEditModal(false);
    setOpenAddToOrgModal(false);
    setSelectedBlueprint(undefined);
    addStatus.reset();
    editStatus.reset();
    addToOrganizationStatus.reset();
  };

  useEffect(() => {
    if (addStatus.successful) {
      toastSuccess("Blueprint added successfully!");
      setOpenAddModal(false);
    }
  }, [addStatus.successful]);

  useEffect(() => {
    if (editStatus.successful) {
      toastSuccess("Blueprint updated successfully!");
      setOpenEditModal(false);
      setSelectedBlueprint(undefined);
    }
  }, [editStatus.successful]);

  useEffect(() => {
    if (addToOrganizationStatus.successful) {
      toastSuccess("Blueprint added to organization successfully!");
      setOpenAddToOrgModal(false);
      setSelectedBlueprint(undefined);
    }
  }, [addToOrganizationStatus.successful]);

  const handleAddSubmit = (name: string, templateJson?: string) => {
    onAdd(name, templateJson);
  };

  const handleEditSubmit = (name: string, id: string, templateJson?: string) => {
    onEdit(id, name, templateJson);
  };

  return (
    <>
      <Segment basic>
        <Header as="h2">Permit Blueprints</Header>
      </Segment>

      <Menu pointing secondary color="green">
        <Menu.Item icon="plus" name="New blueprint" onClick={() => setOpenAddModal(true)} />
      </Menu>

      <Segment basic loading={isLoading}>
        <PermitBlueprintsGrid
          blueprints={blueprints}
          onEditBlueprint={handleEditClicked}
          onAddToOrganization={handleAddToOrganizationClicked}
        />
      </Segment>

      <EditPermitTemplateModal
        open={openAddModal}
        onClose={handleModalsClose}
        onSubmit={handleAddSubmit}
        submitInProgress={addStatus.loading}
        submitErrors={addStatus.errors}
        entityLabel="Blueprint"
      />

      {selectedBlueprint && (
        <EditPermitTemplateModal
          open={openEditModal}
          onClose={handleModalsClose}
          onSubmit={handleEditSubmit}
          submitInProgress={editStatus.loading}
          submitErrors={editStatus.errors}
          entity={selectedBlueprint}
          entityLabel="Blueprint"
        />
      )}

      <AddBlueprintToOrganizationModal
        open={openAddToOrgModal}
        onClose={handleModalsClose}
        onSubmit={onAddToOrganization}
        submitInProgress={addToOrganizationStatus.loading}
        submitErrors={addToOrganizationStatus.errors}
        blueprint={selectedBlueprint}
        organizations={organizations}
      />
    </>
  );
};

export default PermitBlueprintsList;

